import { BANKING_ENTITY_DRIVER_IDS } from '@repo/supergraph/marketplace/bankingEntityDriverIds';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { ProductCard as ProductCardType } from '@repo/supergraph/roundup/base/types';
import isEqual from 'lodash/isEqual';
import { createStore } from 'zustand';

export type SortOption = {
  value: SortByValues;
  label: string;
};

const hasDriverWithId = (offers: ProductCardType[] | undefined, driverId: string) => {
  const normalized = driverId.toLowerCase();
  return offers?.some(offer =>
    offer.marketplaceEntity.drivers?.some(d => d.id.toLowerCase() === normalized),
  );
};

export const getSortOptions = (slug: string, offers?: ProductCardType[]): SortOption[] => {
  if (slug === 'bank-bonuses-and-promotions') {
    return [
      { value: 'starRating' as const, label: 'NerdWallet Rating' },
      { value: 'apy' as const, label: 'Highest APY' },
      { value: 'driverBonus' as const, label: 'Highest Bonus' },
    ];
  }
  if (slug === 'cd-rates') {
    return [
      { value: 'starRating' as const, label: 'NerdWallet Rating' },
      { value: 'apy' as const, label: 'Best APY' },
      { value: 'minimumBalance' as const, label: 'Lowest Min Deposit' },
      { value: 'cdDepositTerm' as const, label: 'Shortest Term Length' },
    ];
  }
  if (slug === 'high-yield-online-savings-account') {
    return [
      { value: 'starRating' as const, label: 'NerdWallet Rating' },
      { value: 'driverApy' as const, label: 'Best APY' },
      { value: 'minimumBalance' as const, label: 'Lowest Min. Balance' },
    ];
  }

  // Default for all other pages: dynamic based on offers
  return [
    { value: 'starRating' as const, label: 'NerdWallet Rating' },
    ...(hasDriverWithId(offers, BANKING_ENTITY_DRIVER_IDS.bonus)
      ? [{ value: 'driverBonus' as const, label: 'Bonus' }]
      : []),
    ...(hasDriverWithId(offers, BANKING_ENTITY_DRIVER_IDS.monthlyFee)
      ? [{ value: 'monthlyFee' as const, label: 'Monthly Fee' }]
      : []),
    ...(hasDriverWithId(offers, BANKING_ENTITY_DRIVER_IDS.apy)
      ? [{ value: 'apy' as const, label: 'APY' }]
      : []),
  ];
};

export type SortByValues =
  | 'starRating'
  | 'apy'
  | 'driverBonus'
  | 'monthlyFee'
  | 'minimumBalance'
  | 'cdDepositTerm'
  | 'driverApy'
  | '';

export type FilterOpenedBar = 'stickyBar' | 'normalBar';
export type FilterOpenedOptions =
  | 'Sort By'
  | 'Product Type'
  | 'Location'
  | 'Term Length'
  | 'Minimum Deposit'
  | 'Minimum Balance'
  | 'Bonus';
export const sortByDefaultValue: SortByValues = '';

export type FilterState = {
  selectedProductTypes: string[];
  selectedLocation?: string;
  selectedTermRanges: string[];
  selectedMinDepositRanges: string[];
  selectedMinBalanceRanges: string[];
  selectedBonusOptions: string[];
};

export const initialFilterState: FilterState = {
  selectedProductTypes: [],
  selectedLocation: undefined,
  selectedTermRanges: [],
  selectedMinDepositRanges: [],
  selectedMinBalanceRanges: [],
  selectedBonusOptions: [],
};

export type OffersFilterStoreState = {
  allOffers: ProductCardType[];
  slug: string;
  sortOptions: SortOption[];
  isLoading: boolean;
  filtersApplied: boolean;
  appliedFilters: FilterState | null;
  filterFormState: FilterState;
  sortBy: SortByValues;
  openedFilters: {
    normalBar: {
      'Term Length': boolean;
      'Minimum Deposit': boolean;
      'Minimum Balance': boolean;
      'Sort By': boolean;
      'Product Type': boolean;
      Location: boolean;
      Bonus: boolean;
    };
    stickyBar: {
      'Term Length': boolean;
      'Minimum Deposit': boolean;
      'Minimum Balance': boolean;
      'Sort By': boolean;
      'Product Type': boolean;
      Location: boolean;
      Bonus: boolean;
    };
  };

  setIsLoading: (loading: boolean) => void;
  handleUpdateFilterFormState: (val: Partial<FilterState>) => void;
  handleClearFilters: (regionCode?: string) => void;
  handleClearFiltersWithGeo: (
    regionCode: string,
    setGeoState: (code: StateAbbreviation) => void,
  ) => void;
  handleLoading: () => void;
  handleApplyFilters: () => { appliedFilters: FilterState; filtersApplied: boolean } | null;
  handleSortBy: (val: SortByValues) => void;
  handleOpenedFilters: (filter: FilterOpenedOptions, bar: FilterOpenedBar, state: boolean) => void;
  switchBarFilters: () => void;
};

const initialState = {
  isLoading: false,
  filtersApplied: false,
  appliedFilters: null,
  filterFormState: initialFilterState,
  sortBy: sortByDefaultValue,
  openedFilters: {
    normalBar: {
      'Sort By': false,
      'Product Type': false,
      Location: false,
      'Term Length': false,
      'Minimum Deposit': false,
      'Minimum Balance': false,
      Bonus: false,
    },
    stickyBar: {
      'Sort By': false,
      'Product Type': false,
      Location: false,
      'Term Length': false,
      'Minimum Deposit': false,
      'Minimum Balance': false,
      Bonus: false,
    },
  },
};

export function createOffersFilterStore(allOffers: ProductCardType[], slug: string) {
  const sortOptions = getSortOptions(slug, allOffers);

  return createStore<OffersFilterStoreState>()((set, get) => ({
    allOffers,
    slug,
    sortOptions,
    ...initialState,
    setIsLoading: loading => set({ isLoading: loading }),

    handleUpdateFilterFormState: (val: Partial<FilterState>) => {
      set({
        filterFormState: {
          ...get().filterFormState,
          ...val,
        },
      });
    },

    handleLoading: () => {
      set({ isLoading: true });
      setTimeout(() => {
        set({ isLoading: false });
      }, 100);
    },

    handleApplyFilters: () => {
      const { filterFormState, appliedFilters, filtersApplied, handleLoading } = get();

      const filtersChanged = !isEqual(
        filterFormState,
        filtersApplied ? appliedFilters : initialFilterState,
      );

      if (!filtersChanged) return null;

      handleLoading();
      const newState = {
        appliedFilters: filterFormState,
        filtersApplied: true,
      };
      set(newState);
      return newState;
    },

    handleClearFilters: (regionCode?: string) => {
      const resetState = {
        ...initialState,
        ...(regionCode && {
          filterFormState: {
            ...initialState.filterFormState,
            selectedLocation: regionCode,
          },
        }),
        filtersApplied: false,
        appliedFilters: null,
      };

      set(() => resetState);
    },

    handleClearFiltersWithGeo: (
      regionCode: string,
      setGeoState: (code: StateAbbreviation) => void,
    ) => {
      setTimeout(() => {
        get().handleClearFilters(regionCode);
        setGeoState(regionCode as StateAbbreviation);
      }, 100);
    },

    handleSortBy: (val: SortByValues) => {
      const { handleLoading } = get();
      set(() => ({ sortBy: val }));
      handleLoading();
    },

    handleOpenedFilters: (filter: FilterOpenedOptions, bar: FilterOpenedBar, state: boolean) => {
      set(previousState => ({
        openedFilters: {
          ...previousState.openedFilters,
          [bar]: { ...previousState.openedFilters[bar], [filter]: state },
        },
      }));
    },

    switchBarFilters: () => {
      set(previousState => ({
        openedFilters: {
          normalBar: previousState.openedFilters.stickyBar,
          stickyBar: previousState.openedFilters.normalBar,
        },
      }));
    },
  }));
}

export type OffersFilterStore = ReturnType<typeof createOffersFilterStore>;
